import SignaturePad from "signature_pad";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, firstValueFrom, throwError } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { PDFDocument, PDFForm } from "pdf-lib";
import { environment } from "../../../environments/environment";
import { AuthService } from "./auth.service";
import { HeaderIntake } from "../../features/intakeForm/models/header-intake.model";
import { buildDownloadFileName, ensurePdfExtension } from "./download-name.util";

@Injectable({ providedIn: "root" })
export class SignaturePDFService {
  private signaturePad: SignaturePad | null = null;
  private isGeneratingSubject = new BehaviorSubject<boolean>(false);
  public isGenerating$ = this.isGeneratingSubject.asObservable();

  constructor(private http: HttpClient,  private authService: AuthService) {}


  async generateIntakePdfBlob(htmlContent: string, header: any): Promise<Blob> {
    const blob = await firstValueFrom(
      this.http.post(
        environment.PDF_SERVER_URL + "api/download-pdf",
        { content: htmlContent, header },
        {
          responseType: "blob",
          headers: { "Content-Type": "application/json" },
        }
      )
    );
    return blob;
  }

  async downloadPDFIntake(htmlContent: string, header: any) {
    try {
      const blob = await this.generateIntakePdfBlob(htmlContent, header);

      const downloadName = ensurePdfExtension(
        buildDownloadFileName("Intake-Form", header?.firstName, header?.lastName)
      );
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      throw new Error("PDF generation failed:" + err.message);
    }
  }
  async downloadPDF(pdfName: string, pdfBytes: Uint8Array) {
    if (pdfBytes) {
      const blob = new Blob([pdfBytes?.buffer.slice(0) as ArrayBuffer], {
        type: "application/pdf",
      });

      // Create URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary <a> element
      const a = document.createElement("a");
      a.href = url;
      a.download = ensurePdfExtension(pdfName);

      //Trigger click
      a.click();

      // Release object URL
      window.URL.revokeObjectURL(url);
    }
  }

  // Initialize signature pad for modal
  initializeSignaturePad(canvas: HTMLCanvasElement): SignaturePad {
    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255, 255, 255, 0)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 1,
      maxWidth: 3,
      throttle: 16,
      minDistance: 5,
    });

    // Resize handler for responsive design
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);
      this.signaturePad?.clear();
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    console.log("resized canvas");
    return this.signaturePad;
  }

  setCheckBox(check: boolean, form: PDFForm, fieldName: string) {
    const checkbox = form.getCheckBox(fieldName);
    if (check!!) {
      checkbox.check();
    } else checkbox.uncheck();
  }

  /**
   * Merge multiple consent PDFs into a single document
   * @param consentPdfs Array of PDF byte arrays to merge
   * @returns Merged PDF as Uint8Array
   */
  async mergeConsentPDFs(consentPdfs: Array<{ bytes: Uint8Array | null, name: string }>): Promise<Uint8Array> {
    const mergedPdf = await PDFDocument.create();

    for (const consentPdf of consentPdfs) {
      if (consentPdf.bytes) {
        const pdfDoc = await PDFDocument.load(consentPdf.bytes);
        const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }
    }

    const mergedBytes = await mergedPdf.save();
    return new Uint8Array(mergedBytes);
  }

    async tryDownloadIntake(
    htmlContent: string, 
    header: HeaderIntake,
    maxRetries: number = 3
  ): Promise<void> {
    let lastError: any = null;

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        await this.downloadPDFIntake(htmlContent, header);
        return;
      } catch (error: any) {
        console.error(`Download attempt ${attempt} failed:`, error);
        lastError = error;
        
        if (attempt > maxRetries) {
          throw new Error(`Failed after ${maxRetries} attempts: ${error.message || "Unknown error"}`);
        }
        
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw lastError || 
      new Error(`Failed to download after ${maxRetries} attempts`);
    
  }

  /**
   * Download a submission as PDF
   * @param formId The ID of the form
   * @param submissionId The ID of the submission
   * @param downloadName The filename for download
   * @returns Promise that resolves when download starts
   */
  async downloadSubmissionPdf(
    formId: string,
    submissionId: string,
    downloadName: string
  ): Promise<void> {
    try {
      // Check for authentication
      const token = this.authService.getToken();
      if (!token) {
        console.error("No authentication token available");
        throw new Error("Authentication required");
      }

      // Set up headers with JWT token
      const headers = new HttpHeaders({
        "x-jwt-token": token,
      });

      // Make the API request
      const response$ = this.http
        .get(
          `${environment.PROJECT_URL}/form/${formId}/submission/${submissionId}/download`,
          { headers, responseType: "blob" }
        )
        .pipe(
          catchError((error) => {
            console.error(
              `Error downloading PDF for submission ${submissionId}:`,
              error
            );

            if (error.status === 403) {
              console.error(
                "Access denied: You may not have permission to download this submission"
              );
              return throwError(
                () =>
                  new Error(
                    "Access denied: You may not have permission to download this submission"
                  )
              );
            } else if (error.status === 401) {
              console.error(
                "Authentication failed. Token may be invalid or expired."
              );
              this.authService.logout();
              return throwError(
                () => new Error("Authentication failed. Please log in again.")
              );
            } else if (error.status === 404) {
              return throwError(
                () => new Error("The requested form or submission was not found")
              );
            } else if (error.status >= 500) {
              return throwError(
                () => new Error("Server error occurred. Please try again later")  
              );
            }

            return throwError(() => error);
          })
        );

      // Wait for the response and handle the blob
      const blob = await firstValueFrom(response$);
      
      // Check for empty response
      if (!blob || blob.size === 0) {
        throw new Error("Received empty document from server");
      }

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);

      // Create a link element and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = ensurePdfExtension(downloadName);
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading submission PDF:", error);
      throw error;
    }
  }

}
